import { useQuery } from '@tanstack/react-query'
import { Check, Circle } from 'lucide-react'
import { vocabularyApi } from '@/api/vocabulary'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { cn } from '@/components/ui/cn'

interface MorphologyBlockProps {
  userWordId: string
}

export function MorphologyBlock({ userWordId }: MorphologyBlockProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['wordFormStats', userWordId],
    queryFn: () => vocabularyApi.getFormStats(userWordId).then((r) => r.data),
  })

  if (isLoading) {
    return (
      <section className="mb-6 flex h-24 items-center justify-center rounded-xl border bg-card">
        <LoadingSpinner />
      </section>
    )
  }

  if (!data?.forms.length) {
    return null
  }

  const discovered = data.forms.filter((form) => form.met)
  const missing = data.forms.filter((form) => !form.met)

  return (
    <section className="mb-6 rounded-xl border bg-card p-4" data-testid="morphology-block">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Morphology
      </h2>

      {discovered.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Discovered</p>
          <ul className="space-y-2">
            {discovered.map((form) => (
              <li
                key={form.surfaceForm}
                className="flex items-start gap-2 text-sm"
                data-testid="morphology-form-discovered"
              >
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="font-medium text-foreground">{form.surfaceForm}</span>
                    {form.teachingTranslation && (
                      <span className="text-foreground">{form.teachingTranslation}</span>
                    )}
                    {form.encounterCount > 0 && (
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {form.encounterCount}×
                      </span>
                    )}
                  </div>
                  {(form.formDescription || form.localizedGrammar) && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {[form.formDescription, form.localizedGrammar].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {missing.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">Not yet encountered</p>
          <ul className="space-y-2">
            {missing.map((form) => (
              <li
                key={form.surfaceForm}
                className="flex items-start gap-2 text-sm"
                data-testid="morphology-form-missing"
              >
                <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className={cn('font-medium', form.teachingTranslation ? 'text-foreground' : 'text-muted-foreground')}>
                      {form.surfaceForm}
                    </span>
                    {form.teachingTranslation && (
                      <span className="text-muted-foreground">{form.teachingTranslation}</span>
                    )}
                  </div>
                  {(form.formDescription || form.localizedGrammar) && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {[form.formDescription, form.localizedGrammar].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
